import type {
  CreditLedgerRow,
  CreditPriceView,
  CreditSettings,
} from '@/features/settings/billing/credits/lib/credits'
import type {
  ApiBillingSettings,
  CreditLedgerEntry,
  GetApiBillingResponse,
  GetCreditSpendStatsResponse,
} from '@/types/generated/public_api'

// ts-proto sends int64 as strings (forceLong=string). Everything numeric is converted here,
// once; an unset optional stays undefined, because unset and 0 mean different things.

const num = (value: string | undefined): number => Number(value ?? 0)
const optNum = (value: string | undefined): number | undefined =>
  value === undefined ? undefined : Number(value)

export interface ApiBillingView {
  /** Unset = the account has never held credits. 0 = it spent everything. Can go negative. */
  balance?: number
  grantedTotal: number
  spentTotal: number
  /** Net of refunds, since Monday 00:00 UTC. */
  spentThisWeek: number
  settings: CreditSettings
  cardBrand?: string
  cardLast4?: string
  /** Unset = credits are not on sale. */
  creditPrice?: CreditPriceView
}

export function fromProtoSettings(settings: ApiBillingSettings | undefined): CreditSettings {
  return {
    spendLimitWeekly: optNum(settings?.spendLimitWeekly),
    refillEnabled: settings?.refillEnabled ?? false,
    refillThreshold: num(settings?.refillThreshold),
    refillCredits: num(settings?.refillCredits),
  }
}

export function toProtoSettings(settings: CreditSettings): ApiBillingSettings {
  return {
    spendLimitWeekly:
      settings.spendLimitWeekly === undefined ? undefined : String(settings.spendLimitWeekly),
    refillEnabled: settings.refillEnabled,
    refillThreshold: String(settings.refillThreshold),
    refillCredits: String(settings.refillCredits),
  }
}

export function toApiBillingView(res: GetApiBillingResponse): ApiBillingView {
  return {
    balance: optNum(res.balance),
    grantedTotal: num(res.grantedTotal),
    spentTotal: num(res.spentTotal),
    spentThisWeek: num(res.spentThisWeek),
    settings: fromProtoSettings(res.settings),
    cardBrand: res.cardBrand || undefined,
    cardLast4: res.cardLast4 || undefined,
    creditPrice: res.creditPrice && {
      unitAmount: num(res.creditPrice.unitAmount),
      currency: res.creditPrice.currency,
      minCredits: num(res.creditPrice.minCredits),
      maxCredits: num(res.creditPrice.maxCredits),
      presetAmounts: res.creditPrice.presetAmounts.map(Number),
    },
  }
}

export function mapLedgerEntry(entry: CreditLedgerEntry): CreditLedgerRow {
  return {
    kind: entry.kind,
    credits: num(entry.credits),
    createdAtMs: num(entry.createdAtMs),
    reference: entry.reference,
    keyId: entry.keyId,
    source: entry.source,
    dataset: entry.dataset,
  }
}

export type CreditBreakdown = Record<string, number>

/** Credits spent, in total and split three ways. The period totals and each day share it. */
export interface CreditSpendView {
  total: number
  byDataset: CreditBreakdown
  bySource: CreditBreakdown
  byKey: CreditBreakdown
}

export interface CreditSpendStatsView extends CreditSpendView {
  days: (CreditSpendView & { day: string })[]
}

type ProtoBreakdown = { [key: string]: string }

const mapBreakdown = (map: ProtoBreakdown): CreditBreakdown =>
  Object.fromEntries(Object.entries(map).map(([key, value]) => [key, Number(value)]))

const mapSpend = (spend: {
  total: string
  byDataset: ProtoBreakdown
  bySource: ProtoBreakdown
  byKey: ProtoBreakdown
}): CreditSpendView => ({
  total: num(spend.total),
  byDataset: mapBreakdown(spend.byDataset),
  bySource: mapBreakdown(spend.bySource),
  byKey: mapBreakdown(spend.byKey),
})

export function mapSpendStats(res: GetCreditSpendStatsResponse): CreditSpendStatsView {
  return { ...mapSpend(res), days: res.days.map((day) => ({ day: day.day, ...mapSpend(day) })) }
}
